import ObjectHeading, { type MetaData } from "@/components/ObjectHeading";
import { ProfileSkeleton } from "@/components/Skeleton";
import { useQuery } from "@apollo/client";
import { FACILITY_DETAILS_HEADER_QUERY, FacilityDetailsHeaderQueryResponse } from "../queries";
import { capitalize } from "lodash";
import PageTitle from "@/components/PageTitle";
import { useLocation, useNavigate } from "react-router-dom";
import Tag from "@/components/Tag";
import { renderEnumDescription } from "@/lib/enumHelpers";
import { renderOperatingStatus } from "../../_utils";
import ArchiveFacilityButton from "./ArchiveFacilityButton";
import UnarchiveFacilityButton from "./UnarchiveFacilityButton";
import { usePinnedNotes } from "@/features/global/components/NotesTable";
import { FACILITY_NOTES_QUERY, FacilityNotesQueryResponse } from "../details/notes/queries";
import { type CRMNote, NotePriorityValues } from "@/types";
import { displayableFacilityLegacyId } from "@/lib/sectionLegacyId";

const FacilityHeading = ({ facilityId }: { facilityId: string }) => {
  const navigate = useNavigate();

  const { data, loading, error } = useQuery<FacilityDetailsHeaderQueryResponse>(FACILITY_DETAILS_HEADER_QUERY, {
    variables: { facilityId }
  });

  const { data: notesData } = useQuery<FacilityNotesQueryResponse>(FACILITY_NOTES_QUERY, {
    variables: {
      id: facilityId,
      input: { priority: [NotePriorityValues.PROMOTE, NotePriorityValues.ALERT] }
    }
  });

  const facilityNotes = notesData?.facilityNotes?.nodes ?? [];
  const { pinnedNotes, shouldAutoOpenNotes } = usePinnedNotes({
    notes: facilityNotes,
    entityId: facilityId,
    sessionStorageKey: "visitedFacilityDetails"
  });

  const headingPinnedNotes = pinnedNotes
    ? {
        ...pinnedNotes,
        onViewAll: () => navigate(`/facilities/${facilityId}/details/notes`),
        getNoteAction: (note: CRMNote) => () =>
          navigate(`/facilities/${facilityId}/details/notes?highlightNote=${note.id}`)
      }
    : undefined;
  const location = useLocation();
  if (loading) return <ProfileSkeleton />;
  if (error) return <div>Error: {error.message}</div>;

  const { facility, operatingStatusOptions } = data!;

  const displayableLegacyId = displayableFacilityLegacyId(facility.legacyId);
  const metaData: MetaData = [
    ...(facility.section ? [{ text: facility.section.name, label: "Section" }] : []),
    ...(displayableLegacyId ? [{ text: displayableLegacyId, label: "Facility ID", copyable: true }] : [])
  ];

  const actionButtons = [
    {
      text: facility.archivedAt ? "Unarchive" : "Archive",
      iconName: facility.archivedAt ? ("unarchive" as const) : ("archive" as const),
      variant: "text" as const,
      onClick: () => {
        const button = document.querySelector(
          `[data-testid=${facility.archivedAt ? "unarchive-facility-button" : "archive-facility-button"}]`
        );
        if (button instanceof HTMLElement) {
          button.click();
        }
      }
    }
  ];

  return (
    <>
      <PageTitle
        pattern={location.pathname}
        title={`Facilities | Facility Details | ${capitalize(location.pathname.split("/").pop())} | ${facility.name}`}
      />
      <ObjectHeading
        title={facility.name}
        iconName="store"
        isDetailsView
        metaData={metaData}
        tag={
          facility.archivedAt != null ? (
            <Tag iconName="archive" tagType="surface" tagStyle="strong">
              Archived
            </Tag>
          ) : (
            renderOperatingStatus(
              facility.operatingStatus,
              renderEnumDescription(facility.operatingStatus, operatingStatusOptions) || "",
              "strong"
            ) || undefined
          )
        }
        actions={actionButtons}
        pinnedNotes={headingPinnedNotes}
        initialNotesDialogOpen={shouldAutoOpenNotes}
      />
      <div>
        <ArchiveFacilityButton facility={facility} />
        <UnarchiveFacilityButton facility={facility} />
      </div>
    </>
  );
};

export function FacilityDetailsHeader({ facilityId }: { facilityId: string }) {
  return (
    <div data-testid="facility-details-header">
      <FacilityHeading facilityId={facilityId} />
    </div>
  );
}
